/**
 * useSpeechRecognition — Web Speech API wrapper for the chat composer.
 *
 * Ported from /personal/loft (2026-05-10). Drops loft's voice-mode sentinel
 * features (force-finalize / mute-unmute cycle) since Stream's composer only
 * needs press-to-talk dictation. Retains the watchdog that recovers WebKit's
 * silent-zombie state.
 *
 * Browser support: WebKit (Safari, Tauri's WKWebView) and Chromium. Falls
 * back gracefully on Firefox / unsupported browsers via the `isSupported`
 * flag — callers should hide the mic button when false.
 */

import { useCallback, useEffect, useRef } from 'react'

// Extend window types for Web Speech API
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SpeechRecognition: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    webkitSpeechRecognition: any
  }
}

export type SpeechRecognitionStatus =
  | 'inactive'
  | 'listening'
  | 'error'

interface UseSpeechRecognitionOptions {
  onTranscript(text: string, isFinal: boolean): void
  onStatusChange(status: SpeechRecognitionStatus): void
  onError(error: string): void
}

/**
 * 8 s is long enough to not trigger during natural inter-sentence pauses
 * but short enough to recover before the user notices a frozen mic.
 */
const WATCHDOG_TIMEOUT_MS = 8000

export function useSpeechRecognition({
  onTranscript,
  onStatusChange,
  onError,
}: UseSpeechRecognitionOptions) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null)
  const activeRef = useRef(false)
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastInterimTextRef = useRef<string>('')

  const isSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)

  const createAndStart = useCallback(() => {
    const SpeechRecognitionClass =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recognition: any = new SpeechRecognitionClass()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    const armWatchdog = () => {
      if (watchdogTimerRef.current !== null) clearTimeout(watchdogTimerRef.current)
      if (!activeRef.current) return
      watchdogTimerRef.current = setTimeout(() => {
        watchdogTimerRef.current = null
        if (recognitionRef.current !== recognition) return
        if (!activeRef.current) return
        const pending = lastInterimTextRef.current
        if (pending) {
          lastInterimTextRef.current = ''
          onTranscript(pending, true)
        }
        recognition.stop()
      }, WATCHDOG_TIMEOUT_MS)
    }

    const clearWatchdog = () => {
      if (watchdogTimerRef.current !== null) {
        clearTimeout(watchdogTimerRef.current)
        watchdogTimerRef.current = null
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      armWatchdog()
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (!result.isFinal) {
          lastInterimTextRef.current = result[0].transcript
        } else {
          lastInterimTextRef.current = ''
        }
        onTranscript(result[0].transcript, result.isFinal)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onerror = (event: any) => {
      if (event.error === 'not-allowed') {
        onError('Microphone permission denied. Please allow microphone access.')
      } else if (event.error === 'no-speech' || event.error === 'aborted') {
        // Benign — silence or programmatic stop. Ignore.
        return
      } else {
        onError(`Speech recognition error: ${event.error}`)
      }
      onStatusChange('error')
    }

    recognition.onstart = () => {
      onStatusChange('listening')
      armWatchdog()
    }

    recognition.onend = () => {
      clearWatchdog()
      lastInterimTextRef.current = ''

      // If this instance is no longer the active one (stop() set ref to null),
      // do not auto-restart.
      if (recognitionRef.current !== recognition) {
        onStatusChange('inactive')
        return
      }

      if (activeRef.current) {
        // Auto-restart while still active (WebKit ends sessions on every silence).
        try {
          recognition.start()
        } catch {
          // Ignore restart errors
        }
      } else {
        onStatusChange('inactive')
      }
    }

    recognitionRef.current = recognition

    try {
      recognition.start()
    } catch (e) {
      onError(`Failed to start: ${e}`)
    }
  }, [onTranscript, onStatusChange, onError])

  const start = useCallback(() => {
    if (!isSupported) {
      onError('Speech recognition is not supported in this environment')
      return
    }
    if (activeRef.current) return
    activeRef.current = true
    createAndStart()
  }, [isSupported, onError, createAndStart])

  const stop = useCallback(() => {
    activeRef.current = false
    lastInterimTextRef.current = ''
    if (watchdogTimerRef.current !== null) {
      clearTimeout(watchdogTimerRef.current)
      watchdogTimerRef.current = null
    }
    const current = recognitionRef.current
    recognitionRef.current = null
    current?.stop()
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      activeRef.current = false
      lastInterimTextRef.current = ''
      if (watchdogTimerRef.current !== null) {
        clearTimeout(watchdogTimerRef.current)
        watchdogTimerRef.current = null
      }
      recognitionRef.current?.stop()
      recognitionRef.current = null
    }
  }, [])

  return { start, stop, isSupported }
}
